import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { channels } from "@/mocks/categories";
import { siteName, siteSlogan } from "@/hooks/useSeo";

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (target: string) =>
    target === "/" ? location.pathname === "/" : location.pathname === target;

  const isCatActive = (key: string) =>
    location.pathname === "/channels" &&
    new URLSearchParams(location.search).get("category") === key;

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    setShowSearch(false);
    navigate(`/channels?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur transition-all ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="mx-auto flex max-w-[1200px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5 py-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-flashlight-fill text-lg"></i>
          </span>
          <span className="leading-none">
            <span className="font-display block text-lg font-bold tracking-wide text-foreground-950">
              {siteName}
            </span>
            <span className="mt-1 block text-[11px] tracking-widest text-foreground-500">
              {siteSlogan}
            </span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
          <Link
            to="/"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
              isActive("/") ? "bg-primary-100 text-primary-700" : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            首页
          </Link>
          {channels.map((c) => (
            <Link
              key={c.key}
              to={`/channels?category=${c.key}`}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isCatActive(c.key) ? "bg-primary-100 text-primary-700" : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {c.short}爆料
            </Link>
          ))}
          <Link
            to="/about"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
              isActive("/about") ? "bg-primary-100 text-primary-700" : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            关于本站
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <button
            type="button"
            onClick={() => setShowSearch((s) => !s)}
            aria-label="搜索"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-search-line text-base"></i>
          </button>
          <Link
            to="/contact"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            <i className="ri-send-plane-line mr-1.5"></i>
            投稿爆料
          </Link>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label="菜单"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 lg:hidden"
          >
            <i className={`ri-${open ? "close" : "menu"}-line text-lg`}></i>
          </button>
        </div>
      </div>

      {showSearch && (
        <form
          onSubmit={submitSearch}
          className="mx-auto flex max-w-[1200px] items-center gap-2 border-t border-background-200/70 px-4 py-3 md:px-6"
        >
          <i className="ri-search-line text-foreground-400"></i>
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜你感兴趣的热点、人物或事件…"
            className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-1.5 text-xs font-medium text-background-50 hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
      )}

      {open && (
        <nav className="border-t border-background-200/70 bg-background-50 px-4 pb-4 pt-2 lg:hidden">
          <div className="grid grid-cols-2 gap-1">
            {[{ key: "__home", name: "首页", to: "/" }, ...channels.map((c) => ({ key: c.key, name: `${c.short}爆料`, to: `/channels?category=${c.key}` }))].map((it) => (
              <Link
                key={it.key}
                to={it.to}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100"
              >
                {it.name}
              </Link>
            ))}
            <Link to="/about" className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100">
              关于本站
            </Link>
            <Link to="/faq" className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100">
              常见问题
            </Link>
          </div>
          <Link
            to="/contact"
            className="mt-3 flex items-center justify-center rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
          >
            <i className="ri-send-plane-line mr-1.5"></i>
            投稿爆料
          </Link>
        </nav>
      )}
    </header>
  );
}